var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { Link, useHistory, useLocation } from "react-router-dom";
import querystring from "query-string";
import { isUrlWhitelisted, translateBackendErrorCode } from "@jti/fe-common";
import { BrowserStorage, DeviceApi, User } from "@jti/rest-api";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";
import { Button, Card, Controller, Form, Notification, Password, Recaptchabar, Separator, TextInput, useConfig, useForm, useTranslations, validationPatterns, } from "@jti/ui";
import { useAemModel } from "../../../contexts/AemModelContext";
import { useLogin } from "../../../hooks/useAuth";
import { useDeviceRegistrationModel } from "../../../hooks/useDeviceRegistrationModel/useDeviceRegistrationModel";
import { useProfile } from "../../../hooks/useProfile";
import { getUserAsJwt } from "../../../utils/user";
import { KakaoLogin } from "../KakaoLogin/KakaoLogin";
import styles from "./LoginForm.module.scss";
export const getDevices = (user) => __awaiter(void 0, void 0, void 0, function* () {
    const deviceAPI = DeviceApi.init(getUserAsJwt());
    try {
        const { data } = yield deviceAPI.myDevices(user.getUser().uuid);
        user.setUser({ is_device_registered: data.length > 0 });
    }
    catch (e) {
        console.error(e);
    }
});
const kakaoAllowedRegistrationErrorCode = "users.authentication::authenticate.authentication_not_found_registration_possible";
const LoginForm = () => {
    const history = useHistory();
    const { search } = useLocation();
    const { __ } = useTranslations();
    const { getUserProfile } = useProfile();
    const { login: loginAction, error, isLoading, isFailed, isSuccess } = useLogin();
    const [isAutoFilled, setIsAutoFilled] = useState(false);
    const loginTimmer = useRef(null);
    const loginInputRef = useRef(null);
    const browserStorage = new BrowserStorage();
    const { brandingInformationEnabled, reCaptchaEnabled, REDIRECT_AFTER_LOGIN_URL, REDIRECT_TO_DEVICE_REGISTRATION_ON_PROFILE, whitelistedDomains, CONSENT_RENEWAL, isKakaoLoginEnabled, kakaoSdkLink, kakaoIntegrityKey, kakaoSdkKey, kakaoButtonTitle, kakaoRedirectUrlAfterLogin, } = useConfig();
    const { isDeviceRegistrationByLeafletEnabled } = useDeviceRegistrationModel();
    const { commonLoginSettings } = useAemModel();
    const { returnUrl, newAccount, s: deviceSerialFromUrl } = querystring.parse(search);
    const form = useForm({
        defaultValues: {
            login: "",
            password: "",
            keepSignIn: false,
        },
    });
    const login = form.watch("login");
    const password = form.watch("password");
    const isLogInButtonDisabled = ((!login.length || !password.length) && !isAutoFilled) || isLoading || isSuccess;
    const redirectToProfile = useCallback(() => {
        const profileRedirectUrl = deviceSerialFromUrl && isDeviceRegistrationByLeafletEnabled
            ? `${REDIRECT_TO_DEVICE_REGISTRATION_ON_PROFILE}?s=${deviceSerialFromUrl}`
            : REDIRECT_AFTER_LOGIN_URL;
        window.location.href =
            typeof returnUrl === "string" && returnUrl && isUrlWhitelisted(returnUrl, whitelistedDomains)
                ? returnUrl
                : profileRedirectUrl;
    }, [deviceSerialFromUrl, isDeviceRegistrationByLeafletEnabled, returnUrl]);
    const handleSubmit = useCallback((loginVariables) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        try {
            const userResponse = yield loginAction(Object.assign({ email: login, password }, loginVariables));
            const user = new User(new BrowserStorage());
            user.setUser(userResponse);
            dispatchTrackingEvent(TrackingEvents.TrackingLoginSuccess, { user_id: userResponse === null || userResponse === void 0 ? void 0 : userResponse.uuid, method: "Email" });
            yield getUserProfile();
            yield getDevices(user);
            if (CONSENT_RENEWAL === "true") {
                const redirectionPath = new URL(window.location.href).searchParams.get("resource");
                if (loginTimmer.current !== null)
                    return;
                if ((redirectionPath === null || redirectionPath === void 0 ? void 0 : redirectionPath.length) && isUrlWhitelisted(redirectionPath, whitelistedDomains)) {
                    loginTimmer.current = window.setTimeout(() => {
                        window.location.href = redirectionPath;
                    }, 500);
                }
                else {
                    loginTimmer.current = window.setTimeout(redirectToProfile, 500);
                }
                return;
            }
            if (loginTimmer.current !== null)
                return;
            loginTimmer.current = window.setTimeout(redirectToProfile, 500);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }
        catch (error) {
            if ((error === null || error === void 0 ? void 0 : error.error_code) !== kakaoAllowedRegistrationErrorCode) {
                const errorText = (_c = (_b = (_a = error === null || error === void 0 ? void 0 : error.errors) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : error === null || error === void 0 ? void 0 : error.message) !== null && _c !== void 0 ? _c : "auth.login.failed";
                const errorMessage = translateBackendErrorCode(error === null || error === void 0 ? void 0 : error.error_code, __(errorText));
                toast.error(errorMessage);
                dispatchTrackingEvent(TrackingEvents.TrackingLoginFail, { method: "Email" });
            }
            return { isError: true };
        }
    }), [loginAction, login, password, CONSENT_RENEWAL, redirectToProfile, whitelistedDomains, browserStorage, history]);
    const loginErrors = useMemo(() => { var _a, _b; return (((_b = (_a = error === null || error === void 0 ? void 0 : error.errors) === null || _a === void 0 ? void 0 : _a["email"]) === null || _b === void 0 ? void 0 : _b.length) ? error.errors["email"].join(" ") : undefined); }, [error]);
    const passwordErrors = useMemo(() => { var _a, _b; return (((_b = (_a = error === null || error === void 0 ? void 0 : error.errors) === null || _a === void 0 ? void 0 : _a["password"]) === null || _b === void 0 ? void 0 : _b.length) ? error === null || error === void 0 ? void 0 : error.errors["password"].join(" ") : undefined); }, [error]);
    useEffect(() => {
        return () => {
            if (loginTimmer.current !== null) {
                window.clearTimeout(loginTimmer.current);
            }
        };
    }, []);
    // comment 18/03/2024 - this is a workaround for the autofill issue in the login form DESP-55537
    useEffect(() => {
        const input = loginInputRef.current;
        if (!isAutoFilled) {
            setTimeout(() => {
                if (input === null || input === void 0 ? void 0 : input.matches(":autofill")) {
                    setIsAutoFilled(true);
                }
            }, 500);
        }
    }, [loginInputRef.current]);
    // end of workaround
    useEffect(() => {
        if (loginErrors) {
            form.setError("login", { message: loginErrors });
        }
        else {
            form.clearErrors("login");
        }
    }, [loginErrors]);
    useEffect(() => {
        if (passwordErrors) {
            form.setError("password", { message: passwordErrors });
        }
        else {
            form.clearErrors("password");
        }
    }, [passwordErrors]);
    const isKakaoEnabled = isKakaoLoginEnabled && kakaoSdkLink && kakaoSdkKey && kakaoIntegrityKey && kakaoRedirectUrlAfterLogin;
    return (_jsx(Card, { children: _jsx(Form, { form: form, onSubmit: handleSubmit, dataTestId: "loginForm", children: _jsxs("div", { className: styles.container, children: [newAccount && _jsx(Notification, { children: __("login.new_account") }), _jsx(Controller, { control: form.control, name: "login", rules: {
                            required: __("login.email.validation.field_required"),
                            pattern: {
                                value: validationPatterns.EMAIL_PATTERN,
                                message: __("login.email.validation.field_pattern"),
                            },
                        }, render: ({ field, fieldState }) => {
                            var _a;
                            return (_jsx(TextInput, Object.assign({}, field, { id: "login_control", ref: loginInputRef, maxLength: 500, placeholder: __("login.email.field_label"), dataTestId: "loginField", label: __("login.email.field_label"), isError: !!(fieldState === null || fieldState === void 0 ? void 0 : fieldState.error), errorText: (_a = fieldState === null || fieldState === void 0 ? void 0 : fieldState.error) === null || _a === void 0 ? void 0 : _a.message, isHelperTextVisible: !!(fieldState === null || fieldState === void 0 ? void 0 : fieldState.error) })));
                        } }), _jsx(Controller, { control: form.control, name: "password", render: ({ field, fieldState }) => {
                            var _a;
                            return (_jsx(Password, Object.assign({}, field, { withValidationRules: false, id: "password_control", placeholder: __("login.password.field_label"), dataTestId: "passwordField", label: __("login.password.field_label"), isError: !!(fieldState === null || fieldState === void 0 ? void 0 : fieldState.error), errorText: (_a = fieldState === null || fieldState === void 0 ? void 0 : fieldState.error) === null || _a === void 0 ? void 0 : _a.message, isHelperTextVisible: !!(fieldState === null || fieldState === void 0 ? void 0 : fieldState.error) })));
                        }, rules: {
                            required: __("login.password.validation.field_required"),
                            validate: (value) => {
                                if (!(value === null || value === void 0 ? void 0 : value.trim())) {
                                    return __("login.password.validation.field_required");
                                }
                                return true;
                            },
                        } }), _jsx("div", { className: styles.forgotLink, children: _jsx(Link, { "data-testid": "forgotPassword", to: "/reset-password", children: _jsx(Button, { size: "medium", color: "tertiary", children: __("login.reset_password.link_label") }) }) }), _jsx(Button, { className: styles.submitButton, type: "submit", color: "primary", disabled: isLogInButtonDisabled, "data-testid": "submitLoginButton", children: __("login.login_button_label") }), isKakaoEnabled && (_jsxs(_Fragment, { children: [_jsx(Separator, { separatorText: commonLoginSettings === null || commonLoginSettings === void 0 ? void 0 : commonLoginSettings.socialLoginSeparatorText }), _jsx("div", { className: styles.socialLogin, children: _jsx(KakaoLogin, { kakaoSdkLink: kakaoSdkLink, integrityKey: kakaoIntegrityKey, sdkKey: kakaoSdkKey, buttonTitle: kakaoButtonTitle, redirectUrlAfterLogin: kakaoRedirectUrlAfterLogin, isLoginInProcess: isLoading, isLoginFailed: isFailed, isLoginSuccess: isSuccess, loginUser: handleSubmit }) })] })), reCaptchaEnabled && brandingInformationEnabled && _jsx(Recaptchabar, { className: styles.recaptchabar })] }) }) }));
};
export default memo(LoginForm);
